import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewEmployeeModal from './NewEmployeeModal';
import EditEmployeeModal from './EditEmployeeModal';
import ViewEmployeeModal from './ViewEmployeeModal';
import ContractorInvoices from './ContractorInvoices';
import EmployeesTableWithShared from './EmployeesTableWithShared';
import { DeleteConfirmationModal, useNotification, TabNavigation, Button } from '../common';
import { hasPermission, canViewEmployeeData, canViewInvoices } from '../../utils/permissions';

const EmployeesList = () => {
  // Import hasPermission
  const { hasPermission } = require('../../utils/permissions');
  
  // Check if user has permission to view contractor invoices
  const canViewInvoices = hasPermission('employees', 'viewInvoices');
  
  // Initialize activeTab from localStorage if it exists and is valid, otherwise default to 'employees'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('employeesActiveTab');
    // Check if savedTab is one of the valid options
    const validTabs = ['employees', 'tutor-invoices'];
    // If the saved tab is 'tutor-invoices' but user doesn't have permission, default to 'employees'
    if (savedTab === 'tutor-invoices' && !canViewInvoices) {
      return 'employees';
    }
    return (savedTab && validTabs.includes(savedTab)) ? savedTab : 'employees';
  });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Initialize limit from localStorage or default to 20
  const initialLimit = parseInt(localStorage.getItem('employeesTableLimit') || '20', 10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: [20, 50, 100].includes(initialLimit) ? initialLimit : 20, // Ensure valid limit
    total: 0,
    pages: 0
  });
  const [csvImportStatus, setCsvImportStatus] = useState({
    loading: false,
    success: null,
    message: '',
    totalRows: 0,
    importedCount: 0,
    errors: []
  });
  const [showCsvGuide, setShowCsvGuide] = useState(false);
  const fileInputRef = React.useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    role_id: '' // Changed from 'role' to 'role_id' for safer filtering
  });
  const [activeFilters, setActiveFilters] = useState({});
  // State for available roles
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showColumnModal, setShowColumnModal] = useState(false);
  
  // Function to format last active time in a relative format
  const formatLastActive = (timestamp) => {
    if (!timestamp) return '—';
    
    const now = new Date();
    const lastActive = new Date(timestamp);
    const diffInSeconds = Math.floor((now - lastActive) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      // If more than a week ago, show the actual date and time
      return lastActive.toLocaleString();
    }
  };
  
  // Function to switch tabs
  const switchTab = (tabName) => {
    // If trying to switch to tutor-invoices tab but don't have permission, stay on employees tab
    if (tabName === 'tutor-invoices' && !canViewInvoices) {
      return;
    }
    
    setActiveTab(tabName);
    localStorage.setItem('employeesActiveTab', tabName);
  };
  
  // Fetch available roles from API
  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token missing when fetching roles');
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/roles', config);
      if (res.data && Array.isArray(res.data)) {
        console.log(`Fetched ${res.data.length} roles for filter dropdown`);
        setAvailableRoles(res.data);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Don't set error state here, it's not critical for the main functionality
    }
  };

  // Fetch employees from API with pagination and filters
  // Modified to accept an optional limit override
  const fetchEmployees = async (page = pagination.page, limitOverride = null) => {
    const currentLimit = limitOverride !== null ? limitOverride : pagination.limit;
    try {
      console.log('Starting employee fetch - setting loading state');
      setLoading(true);
      setError(''); 
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token missing. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Build the parameters including any active filters
      const params = {
        page: page,
        limit: currentLimit, // Use currentLimit here
        ...activeFilters
      };
      
      const config = {
        headers: {
          'x-auth-token': token
        },
        params: params
      };
      
      console.log('Sending API request to fetch employees');
      
      // Using the employees endpoint which gets users without role restrictions
      const res = await axios.get('/api/employees', config);
      
      console.log(`Received employee data: ${res.data.users?.length || 0} employees`);
      
      // The new API returns data under the 'users' key, not 'employees'
      setEmployees(res.data.users || []);
      setPagination({
        page: res.data.pagination.page,
        limit: res.data.pagination.limit,
        total: res.data.pagination.total,
        pages: res.data.pagination.pages
      });
      
      console.log('Employee data processed, turning off loading state');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching employees:', err);
      // Provide more detailed error message
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch employees';
      setError(`Error: ${errorMessage}. Please try again or contact support.`);
      console.log('Error state set, turning off loading state');
      setLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    // Fetch employees for the new page using the current limit
    fetchEmployees(newPage);
  };
  
  // Handle limit change for pagination
  const handleLimitChange = (newLimit) => {
    const limitValue = parseInt(newLimit, 10);
    if (![20, 50, 100].includes(limitValue)) return; // Ignore invalid values

    console.log(`Changing limit to ${limitValue}`);
    // Save the new limit to localStorage
    localStorage.setItem('employeesTableLimit', limitValue.toString());
    // Update pagination state first, resetting page to 1
    setPagination(prev => ({ ...prev, limit: limitValue, page: 1 }));
    // Explicitly fetch employees with page 1 and the new limit value
    fetchEmployees(1, limitValue);
  };
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Handle sorting
  const handleSort = (columnId) => {
    setSortOrder(prevSortOrder => {
      if (sortBy === columnId) {
        return prevSortOrder === 'asc' ? 'desc' : 'asc';
      }
      return 'asc'; // Default to ascending for a new column
    });
    
    setSortBy(columnId);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle employee selection for bulk actions
  const handleEmployeeSelection = (employeeId) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
      setSelectAll(false);
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
      if (selectedEmployees.length + 1 === employees.length) {
        setSelectAll(true);
      }
    }
  };
  
  // Handle select all for bulk actions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.id));
    }
    setSelectAll(!selectAll);
  };
  
  // Apply filters
  const applyFilters = () => {
    // Only include filters with values (not empty strings)
    const newActiveFilters = Object.entries(filters)
      .filter(([_, value]) => value !== '')
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    
    setActiveFilters(newActiveFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
    
    // Close filters panel on mobile
    if (window.innerWidth < 768) {
      setShowFilters(false);
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilters({
      country: '',
      role_id: '' // Changed from 'role' to 'role_id'
    });
    setActiveFilters({});
  };
  
  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;
  
  // Initial fetch when component mounts
  useEffect(() => {
    // Always fetch employees data, regardless of active tab
    fetchEmployees();
    // Fetch available roles for the filter dropdown
    fetchRoles();
  }, []);
  
  // Re-fetch when filters or pagination page changes
  useEffect(() => {
    if (activeTab === 'employees') {
      fetchEmployees(); // Fetch based on current pagination state (page and limit)
    }
    // Removed pagination.limit dependency, handled by handleLimitChange
  }, [activeFilters, activeTab, pagination.page]); 
  
  // Handle escape key to close modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (viewModalOpen) setViewModalOpen(false);
        if (editModalOpen) setEditModalOpen(false);
        if (isModalOpen) setIsModalOpen(false);
        if (showCsvGuide) setShowCsvGuide(false);
        if (deleteModalOpen) setDeleteModalOpen(false);
        if (showFilters) setShowFilters(false);
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [viewModalOpen, editModalOpen, isModalOpen, showCsvGuide, deleteModalOpen, showFilters]);
  
  // Handle new employee creation
  const handleEmployeeCreated = (newEmployee) => {
    // Check if we have all needed data for display, if not, refresh the entire list
    if (!newEmployee || !newEmployee.name || !newEmployee.email) {
      fetchEmployees();
      return;
    }
    
    // Add the new employee to the list
    setEmployees([newEmployee, ...employees]);
  };
  
  // Handle employee update
  const handleEmployeeUpdated = (updatedEmployee) => {
    setEmployees(
      employees.map(emp => 
        emp.id === updatedEmployee.id ? updatedEmployee : emp
      )
    );
  };
  
  // Import useNotification hook
  const { showNotification } = useNotification();
  
  // Handle employee deletion
  const handleDeleteEmployee = async () => {
    try {
      setIsDeleting(true);
      setDeleteError('');
      
      const token = localStorage.getItem('token');
      
      // Use the new employees API endpoint instead of the auth endpoint
      await axios.delete(`/api/employees/${selectedEmployee.id}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      // Remove employee from the list
      setEmployees(employees.filter(emp => emp.id !== selectedEmployee.id));
      
      // Close modals
      setDeleteModalOpen(false);
      setViewModalOpen(false);
      
      setIsDeleting(false);
      
      // Show success notification
      showNotification(`Employee ${selectedEmployee.name} was successfully deleted`, 'success');
    } catch (err) {
      console.error('Error deleting employee:', err);
      setDeleteError(err.response?.data?.message || 'Failed to delete employee');
      setIsDeleting(false);
      
      // Show error notification
      showNotification(`Failed to delete employee: ${err.response?.data?.message || err.message}`, 'error');
    }
  };

  return (
    <div>
      {/* Using shared TabNavigation component */}
      <TabNavigation 
        tabs={[
          {
            id: 'employees',
            label: 'Employee Management',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a3 3 0 00-3-3h-2a3 3 0 00-3 3v1h8z" />
              </svg>
            )
          },
          {
            id: 'tutor-invoices',
            label: 'Contractor Invoices',
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
            ),
            permission: {
              section: 'employees',
              action: 'viewInvoices'
            }
          }
        ]}
        activeTab={activeTab}
        onTabChange={switchTab}
      />
      
      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Employee & User Management</h3>
              <p className="text-sm text-gray-500">Manage all employees and system users in one place</p>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="primary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New Employee
              </Button>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 relative"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span 
                      className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors group"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering parent button click
                        clearFilters();
                        showNotification('Filters cleared', 'success');
                      }}
                      title="Click to clear all filters"
                    >
                      <span className="group-hover:hidden">{activeFilterCount}</span>
                      <span className="hidden group-hover:inline">×</span>
                    </span>
                  )}
                </Button>
                <Button
                  onClick={() => setShowColumnModal(true)}
                  variant="outline"
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                  </svg>
                  Columns
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filters panel */}
          {showFilters && (
            <div className="bg-white p-4 rounded-lg shadow mb-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <select
                    id="country"
                    name="country"
                    value={filters.country}
                    onChange={handleFilterChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">All Countries</option>
                    <option value="Norway">Norway</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Germany">Germany</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="role_id" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    id="role_id"
                    name="role_id"
                    value={filters.role_id}
                    onChange={handleFilterChange}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">All Roles</option>
                    {availableRoles.length > 0 ? (
                      availableRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name} {role.is_system_role ? '(System)' : ''}
                        </option>
                      ))
                    ) : (
                      // Fallback to empty default option if API call fails
                      <option value="" disabled>Loading roles...</option>
                    )}
                  </select>
                </div>
                <div className="flex items-end space-x-2">
                  <Button
                    onClick={applyFilters}
                    variant="primary"
                  >
                    Apply
                  </Button>
                  <Button
                    onClick={clearFilters}
                    variant="outline"
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Loading, error, or employees list */}
          {error ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading employees</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                  <Button
                    onClick={() => fetchEmployees()}
                    variant="danger"
                    className="mt-2 !text-sm !bg-red-100 !text-red-800 !border-red-200 hover:!bg-red-200"
                  >
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden rounded-lg">
              {/* Bulk Actions Bar - only shown when employees are selected */}
              {selectedEmployees.length > 0 && (
                <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      onClick={() => {
                        setSelectedEmployees([]);
                        setSelectAll(false);
                      }}
                      variant="link"
                      className="text-xs"
                    >
                      Clear selection
                    </Button>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        setBulkAction('update');
                        setShowBulkActionModal(true);
                      }}
                      variant="primary"
                      size="sm"
                    >
                      Update
                    </Button>
                    <Button
                      onClick={() => setShowDeleteConfirm(true)}
                      variant="danger"
                      size="sm"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
              
              <EmployeesTableWithShared
                employees={employees}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                selectAll={selectAll}
                setSelectAll={setSelectAll}
                handleSelectAll={handleSelectAll}
                setSelectedEmployee={setSelectedEmployee}
                setViewModalOpen={setViewModalOpen}
                formatLastActive={formatLastActive}
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange} // Pass the handler
                sortBy={sortBy}
                sortOrder={sortOrder}
                handleSort={handleSort}
                showColumnModal={showColumnModal}
                setShowColumnModal={setShowColumnModal}
                loading={loading}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Contractor Invoices Tab - only render if user has permission */}
      {activeTab === 'tutor-invoices' && canViewInvoices && (
        <ContractorInvoices employees={employees} />
      )}

      {/* Show permission denied message if user tries to access invoices tab without permission */}
      {activeTab === 'tutor-invoices' && !canViewInvoices && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Permission Denied</h3>
              <p className="text-sm text-red-700 mt-1">You don't have permission to access contractor invoices.</p>
              <Button
                onClick={() => switchTab('employees')}
                variant="danger"
                className="mt-2 !text-sm !bg-red-100 !text-red-800 !border-red-200 hover:!bg-red-200"
              >
                Return to Employees
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* New Employee Modal */}
      {isModalOpen && (
        <NewEmployeeModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onEmployeeCreated={handleEmployeeCreated} 
        />
      )}
      
      {/* Edit Employee Modal */}
      {editModalOpen && selectedEmployee && (
        <EditEmployeeModal 
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)} 
          employee={selectedEmployee}
          onEmployeeUpdated={handleEmployeeUpdated}
        />
      )}
      
      {/* View Employee Modal */}
      {viewModalOpen && selectedEmployee && (
        <ViewEmployeeModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          employeeId={selectedEmployee?.id} // Pass only the ID
          onEdit={(employeeData) => { // onEdit now receives the fetched data
            setSelectedEmployee(employeeData); // Set selectedEmployee for the Edit modal
            setEditModalOpen(true);
          }}
        />
      )}

      {/* Bulk Action Modal */}
      {showBulkActionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowBulkActionModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Bulk Actions
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You have selected {selectedEmployees.length} {selectedEmployees.length === 1 ? 'employee' : 'employees'}.
                      </p>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Action
                          </label>
                          <select 
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Select an action</option>
                            <option value="role">Update Role</option>
                            <option value="country">Update Country</option>
                            <option value="delete">Delete</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Value
                          </label>
                          <input 
                            type="text"
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="New value"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full sm:ml-3 sm:w-auto text-base sm:text-sm"
                >
                  Apply
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowBulkActionModal(false)}
                  variant="outline"
                  className="mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto text-base sm:text-sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          setDeleteError('');
          
          try {
            const token = localStorage.getItem('token');
            
            // Delete each selected employee one by one using the new endpoint
            for (const employeeId of selectedEmployees) {
              await axios.delete(`/api/employees/${employeeId}`, {
                headers: {
                  'x-auth-token': token
                }
              });
            }
            
            // After all deletions are complete
            setSelectedEmployees([]);
            setSelectAll(false);
            setShowDeleteConfirm(false);
            
            // Show success notification
            showNotification(`Successfully deleted ${selectedEmployees.length} employee${selectedEmployees.length !== 1 ? 's' : ''}`, 'success');
            
            // Refresh the employees list
            fetchEmployees();
          } catch (err) {
            console.error('Error deleting employees:', err);
            setDeleteError(err.response?.data?.message || 'Failed to delete employees');
            // Show error notification
            showNotification(`Failed to delete employees: ${err.response?.data?.message || err.message}`, 'error');
            // Show error for 3 seconds, then close modal
            setTimeout(() => {
              setShowDeleteConfirm(false);
            }, 3000);
          } finally {
            setIsDeleting(false);
          }
        }}
        isLoading={isDeleting}
        title={`Delete ${selectedEmployees.length} ${selectedEmployees.length === 1 ? 'employee' : 'employees'}`}
        message={`Are you sure you want to delete the selected ${selectedEmployees.length} ${selectedEmployees.length === 1 ? 'employee' : 'employees'}? This action cannot be undone.`}
        error={deleteError}
      />
    </div>
  );
};

export default EmployeesList;
